/**
 * Le client de l'Hôtel des Ventes (GDD §20).
 *
 * Tout ce qui touche au marché passe par le serveur — c'est la seule partie du
 * jeu dont l'état n'est pas dans la sauvegarde, parce que c'est la seule où la
 * triche d'un joueur toucherait les autres.
 */
import type { Annonce } from "../sim/marche.ts"

export interface Etal {
  annonces: Annonce[]
  /** Ce qu'il reste à ce compte comme droit de dépôt, et comme places libres. */
  enveloppe: { reste: number; annonces: number } | null
}

const lire = async (r: Response) => {
  const d = await r.json().catch(() => ({})) as Record<string, unknown>
  if (!r.ok) throw new Error(String(d.erreur ?? `Erreur ${r.status}`))
  return d
}

export async function etal(id: string, items: number[] = []): Promise<Etal> {
  const q = new URLSearchParams({ id })
  // Le serveur ne connaît pas les noms d'objets : c'est le client qui traduit
  // un mot cherché en liste d'identifiants, puisqu'il a tout le contenu.
  if (items.length > 0) q.set("items", items.slice(0, 200).join(","))
  const d = await lire(await fetch(`/api/hdv?${q}`))
  return { annonces: (d.annonces ?? []) as Annonce[], enveloppe: (d.enveloppe ?? null) as Etal["enveloppe"] }
}

const poster = (route: string, corps: unknown) =>
  fetch(`/api/hdv/${route}`, {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify(corps),
  }).then(lire)

export const deposer = (id: string, item: number, quantite: number, jet: Record<number, number> | null, prix: number) =>
  poster("deposer", { id, item, quantite, jet, prix }) as Promise<{ id: number; valeur: number; reste: number }>

export const acheter = (id: string, annonce: number) =>
  poster("acheter", { id, annonce }) as Promise<{ ok: true; prix: number; taxe: number }>

export const retirer = (id: string, annonce: number) =>
  poster("retirer", { id, annonce }) as Promise<{ ok: true }>

export const acheterPNJ = (id: string, item: number, quantite: number) =>
  poster("pnj", { id, item, quantite }) as Promise<{ ok: true; prix: number; prochain: number }>

/** Une ligne du journal : ce que le serveur doit au joueur. */
export interface Credit {
  id: number
  kamas: number
  item: number | null
  quantite: number
  jet: Record<number, number> | null
  motif: "vente" | "achat" | "retrait" | "pnj"
  le: number
}

export async function credits(id: string, depuis: number): Promise<Credit[]> {
  const d = await lire(await fetch(`/api/hdv/credits?id=${encodeURIComponent(id)}&depuis=${depuis}`))
  return (d.credits ?? []) as Credit[]
}
